import React, { useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../services/api/axios";
import FormModal from "../form/FormModal";
import { PrimaryButton, OutlineButton } from "../Buttons";

/**
 * Inline buyer creation used by the property Cost Sheet and Create Sale
 * flows. Hits the unified `POST /users?isAccountCreation=true` handler,
 * which atomically creates the user + login + customer_user relation
 * tying the buyer to this developer-tenant and project.
 *
 * Available on both Unified and BuildVizer Plus — Plus tenants need to
 * onboard buyers for ID/sale work even though the bulk Leads-tab
 * add/upload is plan-gated.
 *
 * Props:
 *   open               — show/hide the modal
 *   onClose            — close handler
 *   developerCustomerId — the tenant's customer.id (from userInfo)
 *   projectId          — the project the buyer is being attached to
 *   onCreated(newUser) — fired after the BE create succeeds with the
 *                        full user payload returned from POST /users
 */
const FIELDS_DEFAULT = {
  firstName: "",
  lastName: "",
  emailId: "",
  mobileNoWa: "",
  mobileNoCalling: "",
};

const InlineCreateBuyerModal = ({
  open,
  onClose,
  developerCustomerId,
  projectId,
  onCreated,
}) => {
  const [fields, setFields] = useState(FIELDS_DEFAULT);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFields(FIELDS_DEFAULT);
    setSubmitting(false);
  };

  const close = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const isValid =
    fields.firstName.trim() &&
    fields.lastName.trim() &&
    fields.emailId.trim() &&
    fields.mobileNoWa.trim();

  const handleSubmit = async (e) => {
    e.preventDefault?.();
    if (!isValid || submitting) return;
    if (!developerCustomerId || !projectId) {
      toast.error("Missing project/tenant context — try refreshing the page.");
      return;
    }
    setSubmitting(true);
    try {
      // The BE accepts customerId + projectId as query params and
      // `isAccountCreation=true` to seed the login row alongside the user.
      const response = await axiosInstance.post(
        `/users?customerId=${developerCustomerId}&projectId=${projectId}&isAccountCreation=true`,
        {
          firstName: fields.firstName.trim(),
          lastName: fields.lastName.trim(),
          emailId: fields.emailId.trim(),
          mobileNoWa: fields.mobileNoWa.trim(),
          mobileNoCalling: fields.mobileNoCalling.trim() || fields.mobileNoWa.trim(),
        },
      );
      toast.success("Buyer created — login credentials emailed.");
      onCreated?.(response);
      reset();
      onClose?.();
    } catch (err) {
      const detail = err?.detail || err?.response?.data?.detail || "Failed to create buyer.";
      toast.error(typeof detail === "string" ? detail : "Failed to create buyer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onClose={close}
      title="Create new buyer"
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-3">
          <OutlineButton type="button" onClick={close} disabled={submitting}>
            Cancel
          </OutlineButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? "Creating..." : "Create buyer"}
          </PrimaryButton>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-slate-500">
          Creates the user account, issues login credentials by email, and links the buyer to this project.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              First name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={fields.firstName}
              onChange={onChange}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Last name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={fields.lastName}
              onChange={onChange}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="emailId"
              value={fields.emailId}
              onChange={onChange}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
              required
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Login credentials will be emailed here.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              WhatsApp number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="mobileNoWa"
              value={fields.mobileNoWa}
              onChange={onChange}
              placeholder="+919999999999"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Calling number
            </label>
            <input
              type="tel"
              name="mobileNoCalling"
              value={fields.mobileNoCalling}
              onChange={onChange}
              placeholder="Defaults to WhatsApp number"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-400"
            />
          </div>
        </div>
        {/* Hidden submit so Enter inside any input triggers handleSubmit */}
        <button type="submit" className="hidden" aria-hidden="true" />
      </form>
    </FormModal>
  );
};

export default InlineCreateBuyerModal;
